import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { REACT_APP_BEFORE_LOGIN_REDIRECT_URL } from "../../config";
import Header from "../../components/Header";
import useApp from "../../store/contexts/AppContext";
import { makeStyles } from "@mui/styles";
import { AccountStyle } from "./AccountStyle";
import AccountSkeletonLoader from "./AccountSkeletonLoader";
import EditProfileModal from "./EditProfileModal";
import PaymentCheckoutModal from "./PaymentCheckoutModal";
import MembershipManagementModal from "./MembershipManagementModal";
import DailyBonusWidget from "./DailyBonusWidget";
import AdStatusIndicator from "./AdStatusIndicator";
import AccountActivity from "./AccountActivity";
import Advertisement from "../../components/Others/Advertisement";
import TrendingRooms from "../../components/Others/TrendingRooms";
import NotificationAlert from "./NotificationAlert";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WalletIcon from "@mui/icons-material/Wallet";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import {
  Verified as VerifiedIcon,
  Diamond as DiamondIcon,
  PersonOff as PersonOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import { formatDateDurationForJoinedDate } from "../../utils/otherDateFormatter";
import AvatarImage from "../../images/avatar.png";
import FormatNumberCounter from "../../utils/FormatNumberCounter";
import { TRANSACTION_STATUS, SNACKBAR_VARIANT } from "../../config";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";

const useStyles = makeStyles((theme) => AccountStyle(theme));

export default function Index() {
  const history = useHistory();
  const classes = useStyles();
  const { searchedUsername } = useParams();
  const formattedUsername = searchedUsername
    ? searchedUsername.startsWith("@")
      ? searchedUsername.substring(1).toLowerCase()
      : searchedUsername.toLowerCase()
    : "";

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const status = queryParams.get("status");
  const amount = queryParams.get("amount");
  const tier = queryParams.get("tier");
  const paymentType = queryParams.get("payment_type");
  const fromTier = queryParams.get("from_tier");

  // Redirect if username starts with @
  useEffect(() => {
    if (searchedUsername && searchedUsername.startsWith("@")) {
      history.push(`/${formattedUsername}`);
    }
  }, [searchedUsername, formattedUsername, history]);

  const {
    appState: { accessToken, userData },
    fetchAUserByIdApiCall,
    getUserMembershipInfoApiCall,
    showSnackbar,
  } = useApp();

  const [fetchedUserData, setFetchedUserData] = useState({});
  const [isLoadingSearchedUserData, setIsLoadingSearchedUserData] =
    useState(true);
  const [userIsCurrentLoggedInUser, setUserIsCurrenLoggedInUser] =
    useState(false);
  const [notificationBarOpen, setNotificationBarOpen] = useState(!!status);
  const [userMembershipInfo, setUserMembershipInfo] = useState(null);
  const [userFetchError, setUserFetchError] = useState(null);

  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [openPaymentCheckout, setOpenPaymentCheckout] = useState(false);
  const [openMembership, setOpenMembership] = useState(false);
  const [showActivities, setShowActivities] = useState(false);

  // Modal handlers
  const handleEditProfileOpen = () => setOpenEditProfile(true);
  const handleEditProfileClose = () => setOpenEditProfile(false);
  const handlePaymentCheckoutOpen = () => setOpenPaymentCheckout(true);
  const handlePaymentCheckoutClose = () => setOpenPaymentCheckout(false);
  const handleMembershipOpen = () => setOpenMembership(true);
  const handleMembershipClose = () => setOpenMembership(false);

  // Function to handle ad requirements
  const handleAdRequired = (action) => {
    if (action === "upgrade") {
      handleMembershipOpen();
    }
  };

  // Handler to send a message to the user
  const handleMessageUser = () => {
    if (!fetchedUserData) return;

    // Navigate to messages page with username in path
    history.push(`/messages/${fetchedUserData.username}`);
  };

  // Helper function to get tier-specific badge style
  const getTierBadgeStyle = (tier) => {
    switch (tier) {
      case "standard":
        return { color: "#1976d2", className: classes.standardBadge };
      case "premium":
        return { color: "#d44402", className: classes.premiumBadge };
      case "elite":
        return { color: "#aa00ff", className: classes.eliteBadge };
      default:
        return { color: "white", className: classes.standardBadge };
    }
  };

  // Fetch membership info
  const fetchUserMembershipInfo = async () => {
    try {
      const membershipInfo = await getUserMembershipInfoApiCall();
      if (membershipInfo.success) {
        setUserMembershipInfo(membershipInfo.data);
      } else {
        console.error("Error getting membership info:", membershipInfo.message);
      }
    } catch (error) {
      console.error("Exception fetching membership info:", error);
    }
  };

  // Fetch user data
  const fetchUserData = async () => {
    try {
      setIsLoadingSearchedUserData(true);
      setUserFetchError(null);

      if (!formattedUsername) {
        setUserFetchError("No username provided");
        setIsLoadingSearchedUserData(false);
        return;
      }

      const getQueriedUserData = await fetchAUserByIdApiCall(formattedUsername);

      if (getQueriedUserData.success) {
        setFetchedUserData(getQueriedUserData.data);

        // Check if current user is viewing their own profile
        const isCurrentUser =
          Object.keys(userData).length > 0 &&
          getQueriedUserData.data.username?.toLowerCase() ===
            userData.username?.toLowerCase();

        // Set this state before calling fetchUserMembershipInfo
        setUserIsCurrenLoggedInUser(isCurrentUser);

        // Only fetch membership info if it's the current user's profile
        if (isCurrentUser) {
          // We'll fetch membership info in the useEffect hook instead
        }
      } else {
        setUserFetchError(getQueriedUserData.message || "User not found");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setUserFetchError("Error loading profile");
    } finally {
      setIsLoadingSearchedUserData(false);
    }
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!accessToken) {
      history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
    }
  }, [accessToken, history]);

  // Check if current user is viewing their own profile
  useEffect(() => {
    if (Object.keys(userData).length > 0 && formattedUsername) {
      const isCurrentUser =
        userData.username?.toLowerCase() === formattedUsername;
      setUserIsCurrenLoggedInUser(isCurrentUser);
    }
  }, [userData, formattedUsername]);

  // Fetch membership info when viewing own profile
  useEffect(() => {
    if (userIsCurrentLoggedInUser && Object.keys(userData).length > 0) {
      fetchUserMembershipInfo();
    }
  }, [userIsCurrentLoggedInUser, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data fetch
  useEffect(() => {
    if (formattedUsername) {
      fetchUserData();
    }
  }, [formattedUsername]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current tier information
  const currentTier = userData?.account_type || "basic";
  const isBasic = currentTier === "basic";
  const isMember = !isBasic;

  // Render loading state
  if (isLoadingSearchedUserData) {
    return (
      <div>
        <Header />
        <div className={classes.container}>
          <AccountSkeletonLoader isLoadingSearchedUserData={true} />
        </div>
      </div>
    );
  }

  // Render error state or user not found
  const showErrorScreen =
    userFetchError || !Object.keys(fetchedUserData).length;
  if (showErrorScreen) {
    return (
      <div>
        <Header />
        <div className={classes.container}>
          <Paper elevation={3} className={classes.profileErrorScreen}>
            <PersonOffIcon sx={{ fontSize: 60, color: "#616161", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {userFetchError || "This account was not found"}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classes.profileErrorScreenSmallNotice}
            >
              Try looking up for another person
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/home")}
            >
              Return to Home
            </Button>
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.mode === "dark"
          ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f0f 50%, #0a0a0a 100%)"
          : "linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 50%, #f8f9fa 100%)",
        minHeight: "100vh",
        paddingBottom: 8,
        position: "relative",
        "&::before": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "500px",
          background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.12 : 0.06
          )}, transparent)`,
          pointerEvents: "none",
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "300px",
          background: theme.palette.mode === "dark"
            ? `radial-gradient(ellipse 60% 40% at 50% 100%, ${alpha("#1976d2", 0.08)}, transparent)`
            : `radial-gradient(ellipse 60% 40% at 50% 100%, ${alpha("#7b1fa2", 0.04)}, transparent)`,
          pointerEvents: "none",
          zIndex: 0,
        },
      })}
    >
      <Header />
      {/* Transaction notification */}
      {status && (
        <NotificationAlert
          notificationBarOpen={notificationBarOpen}
          setNotificationBarOpen={setNotificationBarOpen}
          message={`Transaction was ${
            status === TRANSACTION_STATUS.SUCCESS
              ? "successful"
              : "unsuccessful"
          }`}
          status={status === TRANSACTION_STATUS.SUCCESS ? "success" : "error"}
          type={type}
          tier={tier}
          paymentType={paymentType}
          fromTier={fromTier}
          amount={amount}
        />
      )}

      <div className={classes.container} style={{ position: "relative", zIndex: 1 }}>
        {/* Left container - profile content */}
        <div className={classes.topLeftContainer}>
          {/* Profile header section with cover image */}
          <div className={classes.displayPictureSection}>
            <div className={classes.coverPictureSection}>
              {fetchedUserData?.cover_image_url ? (
                <img
                  className={classes.coverPictureContent}
                  src={fetchedUserData.cover_image_url}
                  alt="Cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                  style={{ position: 'relative', zIndex: 1 }}
                />
              ) : null}
              <Box
                sx={{
                  display: fetchedUserData?.cover_image_url ? 'none' : 'block',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)",
                  backgroundSize: '400% 400%',
                  animation: 'gradientShift 15s ease infinite',
                  '@keyframes gradientShift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              />
            </div>

            {/* Profile picture section with responsive layouts */}
            <div
              className={
                fetchedUserData?.cover_image_url
                  ? classes.avatarPictureSection1
                  : classes.avatarPictureSection2
              }
            >
              <div className={classes.avatarPictureContainer}>
                <div className={classes.avatarGradientRing}>
                  <div className={classes.avatarInnerContainer}>
                    <img
                      className={classes.avatarPictureContent}
                      src={fetchedUserData?.profile_image_url ?? AvatarImage}
                      alt="Profile"
                      onError={(e) => {
                        e.target.src = AvatarImage;
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                </div>

                {/* Membership tier badge */}
                {isMember && (
                  <div
                    className={getTierBadgeStyle(currentTier).className}
                    title={`${capitalizeEachFirstWord(currentTier)} Member`}
                  >
                    {currentTier === "standard" ? (
                      <VerifiedIcon sx={{ color: "white" }} />
                    ) : currentTier === "premium" ? (
                      <StarIcon sx={{ color: "white" }} />
                    ) : currentTier === "elite" ? (
                      <DiamondIcon sx={{ color: "white" }} />
                    ) : null}
                  </div>
                )}
              </div>

              {/* Profile action buttons */}
              <div className={classes.editProfileButtonSection}>
                {userIsCurrentLoggedInUser ? (
                  <div>
                    <button
                      type="button"
                      className={classes.editProfileButton}
                      onClick={handleEditProfileOpen}
                    >
                      Edit Profile
                    </button>

                    {/* Membership buttons - different for each tier */}
                    {isMember ? (
                      <button
                        type="button"
                        className={`${classes.editProfileButton} ${
                          currentTier === "standard"
                            ? classes.standardButton
                            : currentTier === "premium"
                            ? classes.premiumButton
                            : classes.eliteButton
                        }`}
                        onClick={handleMembershipOpen}
                      >
                        Manage {capitalizeEachFirstWord(currentTier)}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`${classes.editProfileButton} ${classes.upgradeButton}`}
                        onClick={handleMembershipOpen}
                      >
                        Upgrade Account
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={classes.editProfileButton}
                    onClick={handleMessageUser}
                  >
                    Message
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Username and bio section */}
          <div className={classes.displayPictureAfterSection}>
            <Paper
              className={classes.bioContainer}
              elevation={0}
              sx={{
                borderRadius: 3,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.95))",
                border: (theme) =>
                  `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.08)"
                  }`,
                backdropFilter: "blur(10px)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.3)"
                    : "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
            >
              {/* Header: Display Name and Username */}
              <Box sx={{ mb: 2.5 }}>
                <div className={classes.innerBioContainer1}>
                  <div>
                    {fetchedUserData?.display_name && (
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          mb: 0.5,
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "linear-gradient(135deg, #ff6f00, #d44402, #aa00ff)"
                              : "linear-gradient(135deg, #d44402, #1976d2, #7b1fa2)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundSize: "200% 200%",
                          animation: "gradientShift 8s ease infinite",
                          "@keyframes gradientShift": {
                            "0%": { backgroundPosition: "0% 50%" },
                            "50%": { backgroundPosition: "100% 50%" },
                            "100%": { backgroundPosition: "0% 50%" },
                          },
                        }}
                      >
                        {capitalizeEachFirstWord(fetchedUserData.display_name)}
                      </Typography>
                    )}
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      @{capitalizeEachFirstWord(formattedUsername)}
                      {userIsCurrentLoggedInUser && (
                        <Chip
                          label="Me"
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                          }}
                          color="primary"
                        />
                      )}
                    </Typography>
                  </div>
                  {userIsCurrentLoggedInUser && (
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<WalletIcon />}
                      onClick={handlePaymentCheckoutOpen}
                      sx={{
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        background: "linear-gradient(135deg, #d44402, #ff6f00)",
                        boxShadow: "0 4px 12px rgba(212, 68, 2, 0.3)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #c03e02, #e65100)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(212, 68, 2, 0.4)",
                        },
                      }}
                    >
                      Fund Wallet
                    </Button>
                  )}
                </div>
              </Box>

              {/* Bio Text */}
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)"
                    }`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: fetchedUserData?.bio ? "text.primary" : "text.secondary",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    fontStyle: fetchedUserData?.bio ? "normal" : "italic",
                    fontSize: "1rem",
                  }}
                >
                  {fetchedUserData?.bio || (userIsCurrentLoggedInUser ? "Add a bio to tell people more about yourself" : "No bio yet")}
                </Typography>
              </Box>

              {/* Profile Information Grid */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  {/* Location - Always visible */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOnIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: fetchedUserData?.location ? "normal" : "italic" }}
                    >
                      {fetchedUserData?.location || "No location"}
                    </Typography>
                  </Box>

                  {/* Website - Always visible */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LanguageIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    {fetchedUserData?.website ? (
                      <Typography
                        variant="body2"
                        component="a"
                        href={fetchedUserData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: "primary.main",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {fetchedUserData.website.replace(/^https?:\/\//, "")}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        No website
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Social Media Links - Always visible section */}
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Social:
                  </Typography>
                  {(() => {
                    const socialLinks = fetchedUserData?.social_media_links
                      ? JSON.parse(
                          typeof fetchedUserData.social_media_links === "string"
                            ? fetchedUserData.social_media_links
                            : JSON.stringify(fetchedUserData.social_media_links)
                        )
                      : {};

                    const hasSocialLinks = Object.values(socialLinks).some(link => link && link.trim() !== "");

                    if (!hasSocialLinks) {
                      return (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No social media links
                        </Typography>
                      );
                    }

                    return (
                      <>
                        {socialLinks.twitter && (
                          <IconButton
                            size="small"
                            component="a"
                            href={socialLinks.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1DA1F2",
                              border: "1px solid rgba(29, 161, 242, 0.3)",
                              "&:hover": {
                                backgroundColor: "rgba(29, 161, 242, 0.1)",
                                borderColor: "#1DA1F2"
                              },
                            }}
                          >
                            <TwitterIcon fontSize="small" />
                          </IconButton>
                        )}
                        {socialLinks.instagram && (
                          <IconButton
                            size="small"
                            component="a"
                            href={socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#E4405F",
                              border: "1px solid rgba(228, 64, 95, 0.3)",
                              "&:hover": {
                                backgroundColor: "rgba(228, 64, 95, 0.1)",
                                borderColor: "#E4405F"
                              },
                            }}
                          >
                            <InstagramIcon fontSize="small" />
                          </IconButton>
                        )}
                        {socialLinks.linkedin && (
                          <IconButton
                            size="small"
                            component="a"
                            href={socialLinks.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#0A66C2",
                              border: "1px solid rgba(10, 102, 194, 0.3)",
                              "&:hover": {
                                backgroundColor: "rgba(10, 102, 194, 0.1)",
                                borderColor: "#0A66C2"
                              },
                            }}
                          >
                            <LinkedInIcon fontSize="small" />
                          </IconButton>
                        )}
                        {socialLinks.facebook && (
                          <IconButton
                            size="small"
                            component="a"
                            href={socialLinks.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "#1877F2",
                              border: "1px solid rgba(24, 119, 242, 0.3)",
                              "&:hover": {
                                backgroundColor: "rgba(24, 119, 242, 0.1)",
                                borderColor: "#1877F2"
                              },
                            }}
                          >
                            <FacebookIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </Paper>
          </div>

          {/* Profile Stats Section - Modern Cards */}
          <Box
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: userIsCurrentLoggedInUser ? "repeat(4, 1fr)" : "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {/* Total Coins - Only for current user */}
            {userIsCurrentLoggedInUser && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, rgba(255, 193, 7, 0.15), rgba(255, 152, 0, 0.15))"
                      : "linear-gradient(135deg, rgba(255, 193, 7, 0.08), rgba(255, 152, 0, 0.08))",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 193, 7, 0.2)"
                        : "rgba(255, 193, 7, 0.3)"
                    }`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 8px 24px rgba(255, 193, 7, 0.3)"
                        : "0 8px 24px rgba(255, 193, 7, 0.2)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Total Coins
                  </Typography>
                  <WalletIcon sx={{ color: "#FFC107", fontSize: 20 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#FFC107" }}>
                  {(userData?.available_coins || 0).toLocaleString()}
                </Typography>
              </Paper>
            )}

            {/* Rooms Joined */}
            <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(3, 169, 244, 0.15))"
                      : "linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(3, 169, 244, 0.08))",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(33, 150, 243, 0.2)"
                        : "rgba(33, 150, 243, 0.3)"
                    }`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 8px 24px rgba(33, 150, 243, 0.3)"
                        : "0 8px 24px rgba(33, 150, 243, 0.2)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Rooms Joined
                  </Typography>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#2196F3">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#2196F3" }}>
                  {userData?.total_rooms_joined || 0}
                </Typography>
              </Paper>

              {/* Member Since */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(56, 142, 60, 0.15))"
                      : "linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(56, 142, 60, 0.08))",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(76, 175, 80, 0.2)"
                        : "rgba(76, 175, 80, 0.3)"
                    }`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 8px 24px rgba(76, 175, 80, 0.3)"
                        : "0 8px 24px rgba(76, 175, 80, 0.2)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Member Since
                  </Typography>
                  <CalendarMonthIcon sx={{ color: "#4CAF50", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#4CAF50", fontSize: "1.1rem" }}>
                  {formatDateDurationForJoinedDate(fetchedUserData.created_at)}
                </Typography>
              </Paper>

              {/* Membership Tier */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? currentTier === "elite"
                        ? "linear-gradient(135deg, rgba(170, 0, 255, 0.15), rgba(123, 31, 162, 0.15))"
                        : currentTier === "premium"
                        ? "linear-gradient(135deg, rgba(212, 68, 2, 0.15), rgba(230, 81, 0, 0.15))"
                        : "linear-gradient(135deg, rgba(25, 118, 210, 0.15), rgba(21, 101, 192, 0.15))"
                      : currentTier === "elite"
                      ? "linear-gradient(135deg, rgba(170, 0, 255, 0.08), rgba(123, 31, 162, 0.08))"
                      : currentTier === "premium"
                      ? "linear-gradient(135deg, rgba(212, 68, 2, 0.08), rgba(230, 81, 0, 0.08))"
                      : "linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(21, 101, 192, 0.08))",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? currentTier === "elite"
                          ? "rgba(170, 0, 255, 0.2)"
                          : currentTier === "premium"
                          ? "rgba(212, 68, 2, 0.2)"
                          : "rgba(25, 118, 210, 0.2)"
                        : currentTier === "elite"
                        ? "rgba(170, 0, 255, 0.3)"
                        : currentTier === "premium"
                        ? "rgba(212, 68, 2, 0.3)"
                        : "rgba(25, 118, 210, 0.3)"
                    }`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? currentTier === "elite"
                          ? "0 8px 24px rgba(170, 0, 255, 0.3)"
                          : currentTier === "premium"
                          ? "0 8px 24px rgba(212, 68, 2, 0.3)"
                          : "0 8px 24px rgba(25, 118, 210, 0.3)"
                        : currentTier === "elite"
                        ? "0 8px 24px rgba(170, 0, 255, 0.2)"
                        : currentTier === "premium"
                        ? "0 8px 24px rgba(212, 68, 2, 0.2)"
                        : "0 8px 24px rgba(25, 118, 210, 0.2)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Tier
                  </Typography>
                  {currentTier === "elite" ? (
                    <DiamondIcon sx={{ color: "#AA00FF", fontSize: 20 }} />
                  ) : currentTier === "premium" ? (
                    <StarIcon sx={{ color: "#d44402", fontSize: 20 }} />
                  ) : currentTier === "standard" ? (
                    <VerifiedIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                  ) : (
                    <span />
                  )}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color:
                      currentTier === "elite"
                        ? "#AA00FF"
                        : currentTier === "premium"
                        ? "#d44402"
                        : "#1976d2",
                    fontSize: "1.1rem",
                  }}
                >
                  {capitalizeEachFirstWord(currentTier)}
                </Typography>
              </Paper>
          </Box>

          {/* User additional sections - only shown to the profile owner */}
          {userIsCurrentLoggedInUser && (
            <>
              {/* Daily bonus and ad status */}
              <Box sx={{ mt: 3 }}>
                <DailyBonusWidget classes={classes} />
                {userData?.account_type === "basic" && (
                  <AdStatusIndicator
                    onAdRequired={handleAdRequired}
                    classes={classes}
                  />
                )}
              </Box>

              {/* User activity section - Collapsible */}
              <Box sx={{ mt: 3, mb: 6 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowActivities(!showActivities)}
                  endIcon={showActivities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    mb: showActivities ? 2 : 0,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    padding: "12px 20px",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(212,68,1,0.3)",
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: (theme) => theme.palette.primary.main,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(212,68,1,0.1)"
                          : "rgba(212,68,1,0.05)",
                      transform: "translateY(-2px)",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 4px 12px rgba(212,68,1,0.2)"
                          : "0 4px 12px rgba(212,68,1,0.15)",
                    },
                  }}
                >
                  {showActivities ? "Hide My Activities" : "Show My Activities"}
                </Button>
                {showActivities && (
                  <Box
                    sx={{
                      animation: "fadeIn 0.3s ease-in-out",
                      "@keyframes fadeIn": {
                        from: { opacity: 0, transform: "translateY(-10px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                      },
                    }}
                  >
                    <AccountActivity
                      userIsCurrentLoggedInUser={userIsCurrentLoggedInUser}
                      classes={classes}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </div>

        {/* Right sidebar with ads and trending */}
        <div className={classes.topRightContainer}>
          <Advertisement />
          <TrendingRooms />
        </div>
      </div>

      {/* Modals */}
      {Object.keys(userData).length > 0 && (
        <>
          <EditProfileModal
            openEditProfile={openEditProfile}
            handleEditProfileOpen={handleEditProfileOpen}
            handleEditProfileClose={handleEditProfileClose}
            classes={classes}
            setFetchedUserData={setFetchedUserData}
            fetchedUserData={fetchedUserData}
          />
          <PaymentCheckoutModal
            openPaymentCheckout={openPaymentCheckout}
            handlePaymentCheckoutOpen={handlePaymentCheckoutOpen}
            handlePaymentCheckoutClose={handlePaymentCheckoutClose}
            classes={classes}
            fetchedUserData={fetchedUserData}
          />
          <MembershipManagementModal
            openMembership={openMembership}
            handleMembershipClose={handleMembershipClose}
            classes={classes}
            userMembershipInfo={userMembershipInfo}
            setFetchedUserData={setFetchedUserData}
          />
        </>
      )}
    </Box>
  );
}
